import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  BASE_END_POINT_NOTIFICATION_WS,
  NOTIFICATION_END_POINT
} from '../config';
import { newNotificationReceived } from '../redux/features/notification/notificationsSlice';

/**
 * Custom hook to manage WebSocket connection for real-time notifications
 * @param {string} userId - The user ID to filter notifications
 * @param {boolean} enabled - Whether to enable the WebSocket connection
 * @returns {Object} - WebSocket connection status and methods
 */
export const useNotificationWebSocket = (userId, enabled = true) => {
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 3000; // 3 seconds

  const connect = useCallback(() => {
    if (!userId || !enabled) {
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      // Build WebSocket URL
      const wsUrl = `${BASE_END_POINT_NOTIFICATION_WS}${NOTIFICATION_END_POINT}/ws?user_id=${encodeURIComponent(
        userId
      )}`;

      console.log('📡 [WebSocket] Connecting to:', wsUrl);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('✅ [WebSocket] Connected successfully');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          console.log('📨 [WebSocket] Received notification:', notification);

          // Dispatch the new notification to Redux store
          dispatch(newNotificationReceived({ notification }));
        } catch (error) {
          console.error('❌ [WebSocket] Failed to parse notification:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ [WebSocket] Error:', error);
      };

      ws.onclose = (event) => {
        console.log(
          '🔌 [WebSocket] Connection closed:',
          event.code,
          event.reason
        );

        // Attempt to reconnect if not manually closed and haven't exceeded max attempts
        if (enabled && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `🔄 [WebSocket] Reconnecting... (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, RECONNECT_DELAY);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.error('❌ [WebSocket] Max reconnection attempts reached');
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('❌ [WebSocket] Failed to create connection:', error);
    }
  }, [userId, enabled, dispatch]);

  const disconnect = useCallback(() => {
    console.log('👋 [WebSocket] Disconnecting...');

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close WebSocket connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    reconnectAttemptsRef.current = 0;
  }, []);

  // Connect on mount and when userId or enabled changes
  useEffect(() => {
    if (enabled && userId) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [userId, enabled, connect, disconnect]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    connect,
    disconnect
  };
};
